import {
  useMutation,
  useQueryClient,
  useSuspenseQuery,
} from "@tanstack/react-query"
import { createFileRoute } from "@tanstack/react-router"

import api from "@/utilities/api"

import Cart from "@/globalComponents/organisms/Cart"
import {
  applyReservePromoMutationOptions,
  reserveCheckoutQueryOptions,
} from "@/utilities/hooks/queries/reserve"
import { log } from "@/utilities/logging"

export const Route = createFileRoute(
  "/qr/$qrLookupID/reserve/$epoch/calendars/$calendarID/dates/$year/$month/$day/cart/"
)({
  staticData: {
    pageName: "Cart",
  },
  component: RouteComponent,
})

function RouteComponent() {
  const navigate = Route.useNavigate()
  const {
    sessionState: { plate },
  } = Route.useRouteContext()
  const queryClient = useQueryClient()
  const {
    data: checkoutData,
    refetch,
    isRefetching,
  } = useSuspenseQuery(reserveCheckoutQueryOptions())

  const applyPromotionMutation = useMutation(
    applyReservePromoMutationOptions(queryClient)
  )

  const onCheckoutMutation = useMutation({
    mutationFn: onCheckoutMutationFn,
    throwOnError: true,
  })

  return (
    <Cart
      productType="reserve"
      transaction={checkoutData.transactionDetails}
      cancellationDeadline={checkoutData.cancellationDeadline}
      plate={plate}
      mutations={{
        applyPromotion: applyPromotionMutation,
        resetPromotion: {
          mutate: () => refetch(),
          isPending: isRefetching,
        },
        onGoNext: onCheckoutMutation,
      }}
    />
  )

  async function onCheckoutMutationFn(promoCode: string) {
    if (promoCode) {
      try {
        const cognitoIDToken = await api.auth.getCognitoIDToken()
        await api.reserve.promoPayment.POST({
          cognitoIDToken: cognitoIDToken || "",
          promoCode,
        })
      } catch (e) {
        log({
          level: "warn",
          flow: "reserve",
          scope: "fetchResponse.promoPayment",
          message:
            "Failed to apply promo code at checkout, continuing without it",
          meta: { error: e },
        })
      }
    }

    await navigate({
      to: "/qr/$qrLookupID/reserve/$epoch/calendars/$calendarID/dates/$year/$month/$day/checkout",
    })
  }
}
